'use client';

import Link from 'next/link';

import Text from '@/components/ui/Text';
import { useNavBehavior } from '@/hooks/useNavBehaviour';
import type { NavItem } from '@/lib/contentful/types';
import { cn } from '@/utils/cn';

const NavList = ({
  items,
  variant = 'desktop',
  className,
  itemWrapperClassName,
  linkClassName,
  onItemClick,
}: NavListProps) => {
  const { getHref, isActive, handleClick } = useNavBehavior(items);

  return (
    <div
      className={cn(
        variant === 'desktop' ? 'flex items-center gap-8' : 'flex flex-col',
        className,
      )}
    >
      {items.map((item) => {
        const href = getHref(item);
        const active = isActive(item);

        return (
          <div key={item.id} className={itemWrapperClassName}>
            <Link
              href={href}
              scroll
              onClick={(e) => {
                onItemClick?.(item);
                handleClick(e, item);
              }}
              className={cn(
                'group relative inline-flex items-center',
                variant === 'mobile' && 'w-full py-5',
                linkClassName,
              )}
            >
              <Text
                as="span"
                variant="navItem"
                className={cn('transition-colors', active && 'text-secondary!')}
              >
                {item.name}
              </Text>

              {variant === 'desktop' && (
                <span
                  aria-hidden
                  className={cn(
                    'pointer-events-none absolute left-1/2 -bottom-[8px] h-[3px] w-full -translate-x-1/2 origin-center scale-x-0 bg-secondary transition-transform duration-300 ease-out',
                    'group-hover:scale-x-100',
                    active && 'scale-x-100',
                  )}
                />
              )}
            </Link>
          </div>
        );
      })}
    </div>
  );
};

export default NavList;

/** Re-exported for backward compatibility with header/footer/hook consumers */
export type MenuItem = NavItem;

type NavListProps = {
  items: MenuItem[];
  className?: string;
  itemWrapperClassName?: string;
  linkClassName?: string;
  variant: 'desktop' | 'mobile';
  onItemClick?: (item: MenuItem) => void;
};
